import { useCallback, useState } from 'react';

type CSVValue = string | number | Date | null | undefined;

export interface CSVColumn<T, V extends CSVValue = CSVValue> {
  key: string;
  header: string;
  accessor: (item: T) => V;
  formatter?: (value: NonNullable<V>) => string;
}

interface UseCSVExportOptions<T> {
  data: T[] | undefined;
  columns: CSVColumn<T>[];
  filename: string;
  dateFormat?: string;
  locale?: string;
}

interface CSVExportResult {
  downloadCSV: () => void;
  isExporting: boolean;
  error: string | null;
}

export function useCSVExport<T>({
  data,
  columns,
  filename,
  locale = 'en-US',
}: UseCSVExportOptions<T>): CSVExportResult {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const escapeCSVField = useCallback((field: string): string => {
    const stringField = String(field ?? '');
    if (
      stringField.includes(',') ||
      stringField.includes('"') ||
      stringField.includes('\n') ||
      stringField.includes('\r')
    ) {
      return `"${stringField.replace(/"/g, '""')}"`;
    }
    return stringField;
  }, []);

  const formatValue = useCallback(
    (value: CSVValue, formatter?: (value: NonNullable<CSVValue>) => string): string => {
      if (value == null) return '';

      if (formatter) {
        return formatter(value as NonNullable<CSVValue>);
      }

      if (value instanceof Date) {
        return value.toLocaleString(locale);
      }

      return String(value);
    },
    [locale]
  );

  const downloadCSV = useCallback(async () => {
    if (!data || data.length === 0) {
      setError('No data available for export');
      return;
    }

    setIsExporting(true);
    setError(null);

    try {
      const csvRows = [columns.map((col) => col.header)];

      for (const item of data) {
        const row = columns.map((col) => {
          const value = col.accessor(item);
          const formattedValue = formatValue(value, col.formatter);
          return escapeCSVField(formattedValue);
        });
        csvRows.push(row);
      }

      const csvString = csvRows.map((row) => row.join(',')).join('\n');

      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvString], {
        type: 'text/csv;charset=utf-8',
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const timestampedFilename = `${filename}.csv`;

      link.href = url;
      link.download = timestampedFilename;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      await new Promise((resolve) => setTimeout(resolve, 300));
      setIsExporting(false);
    }
  }, [data, columns, filename, escapeCSVField, formatValue]);

  return {
    downloadCSV,
    isExporting,
    error,
  };
}
